import { useState, useRef, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EMOJI_CATEGORIES } from './sms-editor-constants';
import type { EmojiItem } from './sms-editor-constants';

interface SmsEmojiPickerProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
}

interface EmojiGridProps {
  onSelect: (emoji: string) => void;
}

/**
 * The searchable, categorised emoji grid on its own, with no positioning or
 * outside-click handling, so a host that already owns those (a Popover) can
 * render it. SmsEmojiPicker below wraps it in the absolutely positioned panel
 * the SMS editors use.
 */
export function EmojiGrid({ onSelect }: EmojiGridProps) {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(EMOJI_CATEGORIES[0].id);

  const flat = useMemo<EmojiItem[]>(() =>
    EMOJI_CATEGORIES.flatMap((cat) =>
      cat.items.map(([e, k]) => ({
        e,
        k: (k + ' ' + cat.label).toLowerCase(),
      })),
    ),
  []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (q) return flat.filter((item) => item.k.includes(q));
    return flat.filter((item) => {
      const cat = EMOJI_CATEGORIES.find((c) =>
        c.items.some(([em]) => em === item.e),
      );
      return cat?.id === activeCategory;
    });
  }, [search, activeCategory, flat]);

  return (
    <>
      <div className="p-2 pb-1 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded focus:border-sky-400 focus:outline-none"
            placeholder="Search emojis..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
      </div>

      {!search && (
        <div className="flex gap-0.5 px-2 border-b border-slate-100 flex-shrink-0">
          {EMOJI_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={cn(
                'px-1.5 py-1 text-base rounded-t transition-colors',
                activeCategory === cat.id ? 'bg-slate-100 opacity-100' : 'opacity-50 hover:opacity-80',
              )}
              title={cat.label}
            >
              {cat.icon}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-2 flex flex-wrap gap-0.5 content-start">
        {filtered.map((item, i) => (
          <button
            key={i}
            onClick={() => onSelect(item.e)}
            className="w-8 h-8 flex items-center justify-center rounded hover:bg-slate-100 text-lg cursor-pointer"
          >
            {item.e}
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="w-full text-center text-sm text-slate-400 py-4">No emojis found</p>
        )}
      </div>
    </>
  );
}

export default function SmsEmojiPicker({ onSelect, onClose }: SmsEmojiPickerProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div
      ref={panelRef}
      className="absolute bottom-full left-0 mb-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 flex flex-col"
      style={{ width: '420px', height: '380px' }}
    >
      <EmojiGrid onSelect={onSelect} />
    </div>
  );
}
